import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ArrowCircleLeft,
  ArrowCircleRight,
  CaretLeft,
  CaretRight,
  FileText,
  User,
} from 'phosphor-react';
import { useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FeedbackModal } from '../../../../shared/FeedbackModal';
import { PageLayout } from '../../../../shared/PageLayout';
import styles from './create-budget-form.module.css';
import { CustomerForm } from './steps/customerForm';
import { BudgetFormDTO } from './BudgetFormDTO';
import { defaultBudget } from '../createBudgetMockup';
import { ServicesForm } from './steps/serviceForm';
import { Steps } from '../../OrderService/components/Steps';
import { formValidationSchema } from './validation';

export type BudgetStep = 'customer' | 'service';

export const CreateBudgetForm = () => {
  const [selectedStep, setSelectedStep] = useState<BudgetStep>('customer');
  const methods = useForm<BudgetFormDTO>({
    defaultValues: defaultBudget,
    resolver: yupResolver(formValidationSchema),
    mode: 'onChange',
  });

  const navigate = useNavigate();

  const CurrentForm = useMemo(() => {
    if (selectedStep === 'customer') return <CustomerForm />;
    return <ServicesForm />;
  }, [selectedStep]);

  const steps = useMemo(
    () => [
      {
        label: 'Dados do cliente',
        icon: (
          <User
            size={24}
            weight={selectedStep === 'customer' ? 'fill' : 'bold'}
          />
        ),
        value: 'customer' as BudgetStep,
        warning: false,
      },
      {
        label: 'Dados do serviço',
        icon: (
          <FileText
            size={24}
            weight={selectedStep === 'service' ? 'fill' : 'bold'}
          />
        ),
        value: 'service' as BudgetStep,
        warning: false,
      },
    ],
    [selectedStep],
  );

  const handleChangeStep = async (direction: 'previous' | 'next') => {
    const currentIndex = steps.findIndex(step => step.value === selectedStep);

    if (!(await methods.trigger()) && direction === 'next') {
      return;
    }

    if (direction === 'next' && currentIndex < steps.length - 1) {
      setSelectedStep(steps[currentIndex + 1].value);
    } else if (direction === 'previous' && currentIndex > 0) {
      setSelectedStep(steps[currentIndex - 1].value);
    }
  };

  return (
    <FormProvider {...methods}>
      {/* <FeedbackModal isOpen={isLoading} variant="sending" /> */}

      <div className={styles.container}>
        <PageLayout.Root
          title="CRIAR ORÇAMENTO"
          handleNavigation={() => navigate(-1)}
        />
        <div className={styles.content}>
          <button
            type="button"
            className={styles['change-step-btn']}
            style={{
              opacity: selectedStep === 'customer' ? 0 : 1,
            }}
            onClick={() => handleChangeStep('previous')}
          >
            <CaretLeft size={24} weight="bold" color="var(--white)" />
          </button>
          <section className={styles['form-section']}>
            <Steps
              onClick={async (step: string) => {
                if (await methods.trigger()) {
                  setSelectedStep(step as BudgetStep);
                }
              }}
              selectedStep={selectedStep}
              steps={steps}
              ordered
            />
            {CurrentForm}
            <div className={styles['mobile-actions-container']}>
              <button
                type="button"
                className={styles['primary-btn']}
                onClick={() => handleChangeStep('previous')}
                style={{
                  display: selectedStep === 'customer' ? 'none' : 'flex',
                }}
              >
                <ArrowCircleLeft size={24} />
                Voltar
              </button>
              <button
                type="button"
                className={styles['second-btn']}
                onClick={() => handleChangeStep('next')}
                style={{
                  display: selectedStep === 'service' ? 'none' : 'flex',
                }}
              >
                Próximo
                <ArrowCircleRight size={24} />
              </button>
            </div>
          </section>
          <button
            type="button"
            className={styles['change-step-btn']}
            onClick={() => handleChangeStep('next')}
            style={{
              opacity: selectedStep === 'service' ? 0 : 1,
            }}
          >
            <CaretRight size={24} weight="bold" color="var(--white)" />
          </button>
        </div>
      </div>
    </FormProvider>
  );
};
